<template>
  <div class="rd-screen">
    <report-tools :copy="hasRows" @copy="copyTable"></report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state v-else-if="!hasRows" icon="mdi-chart-bar" :title="$t('no_data')" :text="$t('select_date_and_filters')"></report-state>

    <div v-else class="rd-card rd-card--scroll">
      <table class="rd-tbl">
        <thead>
          <tr>
            <th scope="col" class="rd-col-num">#</th>
            <th scope="col" :aria-sort="ariaSort('name')">
              <button type="button" @click="sortBy('name')">{{ $t("restaurant") }} {{ sortArrow("name") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('meals')">
              <button type="button" @click="sortBy('meals')">{{ $t("meal_count") }} {{ sortArrow("meals") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('amount')">
              <button type="button" @click="sortBy('amount')">{{ $t("amount") }}{{ currencySuffix }} {{ sortArrow("amount") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('company')">
              <button type="button" @click="sortBy('company')">
                {{ $t("company_amount") }}{{ currencySuffix }} {{ sortArrow("company") }}
              </button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('employee')">
              <button type="button" @click="sortBy('employee')">
                {{ $t("employee_amount") }}{{ currencySuffix }} {{ sortArrow("employee") }}
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rows" :key="row.key">
            <td class="rd-num rd-muted">{{ index + 1 }}</td>
            <td class="is-name" :title="row.name">{{ row.name }}</td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.meals }">{{ row.meals }}</td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.amount }">{{ money(row.amount) }}</td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.company }">{{ money(row.company) }}</td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.employee }">{{ money(row.employee) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <td>{{ $t("total") }}</td>
            <td class="is-right rd-num">{{ sums.meals }}</td>
            <td class="is-right rd-num">{{ money(sums.amount) }}</td>
            <td class="is-right rd-num">{{ money(sums.company) }}</td>
            <td class="is-right rd-num">{{ money(sums.employee) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import CompanyMultiRestaurantTabRestaurantSummary from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabRestaurantSummary.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasMultiFeed from "../../../hasMultiFeed";

/**
 * Pregled po restoranima firme "više restorana" na računaru: po restoranu broj obroka, ukupan iznos i koliko je
 * platila firma, a koliko radnik. Restorane i period čita iz zajedničke trake. Nasleđuje učitavanje, štampu i Excel
 * iz starog taba.
 */
export default {
  name: "CompanyDesktopMultiRestaurantSummary",
  extends: CompanyMultiRestaurantTabRestaurantSummary,
  mixins: [hasMultiFeed, hasTableSort],
  components: { ReportState, ReportTools },
  data: () => ({
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    busy() {
      return this.loading;
    },
    /** Ima podataka kad je bar jedan restoran imao obroke ili iznos (stari tab navodi i restorane sa nulama). */
    hasRows() {
      return this.rows.some((row) => row.meals || row.amount);
    },
    currencySuffix() {
      return this.majorityRestaurantCurrency ? ` (${this.majorityRestaurantCurrency})` : "";
    },
    /** Restorani bez zbirnog reda starog taba. */
    rows() {
      return this.sortRows(
        this.restaurantSummary
          .filter((item) => !item.isTotal)
          .map((item) => ({
            key: String(item.id),
            name: this.toLatin(item.restaurant || ""),
            meals: item.meal_count,
            amount: item.total_amount,
            company: item.company_amount,
            employee: item.employee_amount,
          }))
      );
    },
    sums() {
      return this.rows.reduce(
        (sums, row) => ({
          meals: sums.meals + row.meals,
          amount: sums.amount + row.amount,
          company: sums.company + row.company,
          employee: sums.employee + row.employee,
        }),
        { meals: 0, amount: 0, company: 0, employee: 0 }
      );
    },
    kpis() {
      return [
        { key: "meals", label: this.$t("meal_count"), value: this.sums.meals, tone: "inverse" },
        { key: "amount", label: this.$t("amount"), value: this.money(this.sums.amount) },
        { key: "company", label: this.$t("company_amount"), value: this.money(this.sums.company) },
      ];
    },
  },
  methods: {
    money(value) {
      return (Number(value) || 0).toFixed(2);
    },
    async copyTable() {
      const headers = [this.$t("restaurant"), this.$t("meal_count"), this.$t("amount"), this.$t("company_amount"), this.$t("employee_amount")];
      const rows = this.rows.map((row) => [row.name, row.meals, this.money(row.amount), this.money(row.company), this.money(row.employee)]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
